import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import ModalCloseButton from './ModalCloseButton';
import SearchBar from './SearchBar';

/**
 * SelectorOverlay: 공통 선택 오버레이
 * - 모바일(< 640px): MOVV 스타일 전체 페이지 (뒤로가기 + 검색 + 결과)
 * - PC(≥ 640px): 중앙 모달
 */
export default function SelectorOverlay({
  displayLabel,
  placeholder,
  className = '',
  variant = 'default',
  open,
  onOpen,
  onClose,
  maxWidth = '520px',
  search,
  onSearchChange,
  searchPlaceholder,
  title,
  children,
}) {
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ── Body scroll lock ── */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  /* ── Escape ── */
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  /* ── Auto focus search input ── */
  useEffect(() => {
    if (open && isMobile) {
      setTimeout(() => searchInputRef.current?.focus(), 150);
    }
  }, [open, isMobile]);

  return (
    <div className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={onOpen}
        className={`w-full text-left transition font-medium h-12 px-4 pr-10 text-[15px] leading-tight ${
          variant === 'borderless'
            ? 'bg-transparent border-0'
            : `bg-white border rounded-lg ${
                open
                  ? 'border-orange-500 ring-1 ring-orange-500/20'
                  : 'border-mono-200 hover:border-mono-300'
              }`
        } ${displayLabel ? 'text-mono-900' : 'text-mono-500'}`}
      >
        {displayLabel || placeholder}
      </button>
      <Icon
        name="chevron-down"
        size={16}
        className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${
          open ? 'rotate-180 text-orange-500' : 'text-mono-400'
        }`}
      />

      {/* ===== 모바일: 전체 페이지 (MOVV 스타일) ===== */}
      {open && isMobile && createPortal(
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
          <style>{`body { overflow: hidden; }`}</style>

          {/* Header: 뒤로가기 + 검색바 + 닫기 */}
          <div className="shrink-0 flex items-center gap-2 px-3 pt-3 pb-2 border-b border-mono-100">
            <button type="button" onClick={onClose} className="p-1.5 -ml-1">
              <Icon name="chevron-left" size={22} className="text-mono-900" />
            </button>
            <div className="flex-1 min-w-0">
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={onSearchChange}
                placeholder={searchPlaceholder}
                className="w-full h-10 px-3 bg-mono-50 rounded-lg text-sm font-medium text-mono-900 placeholder:text-mono-400 outline-none focus:bg-white focus:ring-1 focus:ring-orange-500 transition"
              />
            </div>
            <button type="button" onClick={onClose} className="p-1.5">
              <Icon name="close" size={20} className="text-mono-400" />
            </button>
          </div>

          {/* Title */}
          {title && (
            <div className="shrink-0 px-4 pt-3 pb-2">
              <h3 className="text-sm font-bold text-mono-900">{title}</h3>
            </div>
          )}

          {/* Content — 전체 높이 스크롤 */}
          <div className="flex-1 overflow-y-auto overscroll-contain -webkit-overflow-scrolling-touch" style={{ WebkitOverflowScrolling: 'touch' }}>
            {children}
          </div>
        </div>,
        document.body
      )}

      {/* ===== PC: 중앙 모달 ===== */}
      {open && !isMobile && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={onClose} />
          <div
            ref={panelRef}
            className="relative bg-white rounded-2xl mx-4 shadow-2xl overflow-hidden animate-dropdown"
            style={{ maxWidth, width: '100%', maxHeight: '80vh' }}
          >
            {/* Search + Close */}
            <div className="flex items-center gap-4 px-6 pt-6 pb-3">
              <div className="flex-1 min-w-0">
                <SearchBar
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={onSearchChange}
                  leftIcon="search-filled"
                />
              </div>
              <ModalCloseButton onClick={onClose} className="shrink-0" />
            </div>

            {/* Title */}
            {title && (
              <div className="px-6 pb-3">
                <h3 className="text-base font-bold text-mono-900">{title}</h3>
              </div>
            )}

            {/* Content */}
            <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: 'calc(80vh - 130px)' }}>
              {children}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
